import React, { useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const statusColor = (s) => ({ confirmed: "bg-green-500/80", pending_payment: "bg-amber-500/80", completed: "bg-pink-500/80", cancelled: "bg-red-500/60" }[s] || "bg-white/40");

export default function AdminCalendar({ bookings }) {
  const [view, setView] = useState("month");
  const [cursor, setCursor] = useState(new Date());

  const byDate = {};
  bookings.forEach((b) => { (byDate[b.date] = byDate[b.date] || []).push(b); });

  const shift = (dir) => {
    const c = new Date(cursor);
    if (view === "month") c.setMonth(c.getMonth() + dir);
    else if (view === "week") c.setDate(c.getDate() + dir * 7);
    else c.setDate(c.getDate() + dir);
    setCursor(c);
  };

  const weekStart = () => { const s = new Date(cursor); s.setDate(s.getDate() - s.getDay()); return s; };
  const title = view === "month"
    ? `${MONTHS[cursor.getMonth()]} ${cursor.getFullYear()}`
    : view === "day"
      ? cursor.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })
      : (() => { const s = weekStart(); const e = new Date(s); e.setDate(e.getDate() + 6); return `${MONTHS[s.getMonth()]} ${s.getDate()} – ${MONTHS[e.getMonth()]} ${e.getDate()}`; })();

  const renderMonth = () => {
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const startPad = first.getDay();
    const daysInMonth = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < startPad; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(cursor.getFullYear(), cursor.getMonth(), d));
    return (
      <div>
        <div className="grid grid-cols-7 gap-1 mb-1">{DOW.map((d) => (<div key={d} className="text-center text-xs text-zinc-500 font-semibold py-1">{d}</div>))}</div>
        <div className="grid grid-cols-7 gap-1">
          {cells.map((c, i) => {
            if (!c) return <div key={i} className="min-h-[92px]" />;
            const key = ymd(c);
            const list = (byDate[key] || []).filter((b) => b.status !== "cancelled");
            const today = ymd(new Date()) === key;
            return (
              <div key={i} className={`min-h-[92px] rounded-lg border p-1.5 ${today ? "border-pink-500" : "border-white/10"} bg-[#121212]`} data-testid={`cal-day-${key}`}>
                <p className={`text-xs ${today ? "text-pink-500 font-bold" : "text-zinc-500"}`}>{c.getDate()}</p>
                <div className="mt-1 space-y-1">
                  {list.slice(0, 3).map((b) => (<div key={b.id} className={`text-[10px] truncate rounded px-1 py-0.5 text-white ${statusColor(b.status)}`} title={`${b.full_name} · ${b.time_slot}`}>{b.time_slot} {b.full_name}</div>))}
                  {list.length > 3 && <p className="text-[10px] text-zinc-500">+{list.length - 3} more</p>}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderList = (dates) => (
    <div className={`grid gap-2 ${dates.length > 1 ? "sm:grid-cols-7" : ""}`}>
      {dates.map((c) => {
        const key = ymd(c);
        const list = byDate[key] || [];
        return (
          <div key={key} className="rounded-lg border border-white/10 bg-[#121212] p-2 min-h-[130px]" data-testid={`cal-col-${key}`}>
            <p className="text-xs text-zinc-400 font-semibold mb-2">{DOW[c.getDay()]} {c.getDate()}</p>
            <div className="space-y-1">
              {list.length === 0 && <p className="text-[10px] text-zinc-600">—</p>}
              {list.map((b) => (<div key={b.id} className={`text-[11px] rounded px-1.5 py-1 text-white ${statusColor(b.status)}`}>{b.time_slot} · {b.full_name}</div>))}
            </div>
          </div>
        );
      })}
    </div>
  );

  const renderWeek = () => { const s = weekStart(); const days = [...Array(7)].map((_, i) => { const d = new Date(s); d.setDate(s.getDate() + i); return d; }); return renderList(days); };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-2">
          <button onClick={() => shift(-1)} className="rounded-lg border border-white/15 p-2 hover:bg-white/5" data-testid="cal-prev"><ChevronLeft size={16} /></button>
          <button onClick={() => shift(1)} className="rounded-lg border border-white/15 p-2 hover:bg-white/5" data-testid="cal-next"><ChevronRight size={16} /></button>
          <button onClick={() => setCursor(new Date())} className="text-sm rounded-lg border border-white/15 px-3 py-2 hover:bg-white/5" data-testid="cal-today">Today</button>
          <h2 className="text-lg font-bold ml-2" data-testid="cal-title">{title}</h2>
        </div>
        <div className="flex gap-1 bg-[#121212] border border-white/10 rounded-lg p-1">
          {["day", "week", "month"].map((v) => (<button key={v} onClick={() => setView(v)} className={`text-xs px-3 py-1.5 rounded-md capitalize ${view === v ? "bg-pink-600 text-white" : "text-zinc-400"}`} data-testid={`cal-view-${v}`}>{v}</button>))}
        </div>
      </div>
      {view === "month" ? renderMonth() : view === "week" ? renderWeek() : renderList([cursor])}
    </div>
  );
}
